import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CLOUD_NEW_FEATURES_CONFIG } from 'reactApp/appHelpers/configHelpers';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { CLOUD_NEW_FEATURES_TOOLTIP_ID_MOBILE } from 'reactApp/ui/CloudNewFeatures/CloudNewFeatures.helpers';
import { FeaturesButton } from 'reactApp/ui/CloudNewFeatures/FeaturesButton/FeaturesButton';
import { FeaturesTooltip } from 'reactApp/ui/CloudNewFeatures/FeaturesTooltip/FeaturesTooltip';
import { renderMobileCloudNewFeaturesModal } from 'reactApp/ui/Mobile/CloudNewFeaturesModal/CloudNewFeaturesModal.helpers';
import { LeftDialog } from 'reactApp/ui/Mobile/LeftDialog/LeftDialog';
import { Logo } from 'reactApp/ui/Mobile/Logo/Logo';
import { UserBlock } from 'reactApp/ui/Mobile/UserBlock/UserBlock';
import { Space } from 'reactApp/ui/Space/Space';
import { treeItemsFlatten } from 'reactApp/ui/TreeComponent/TreeComponent.data';
import { TreeNodeData } from 'reactApp/ui/TreeComponent/TreeNode.types';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import styles from './LeftMenu.css';
import { MenuItem } from './MenuItem/MenuItem';

interface Props {
    onClose: () => void;
    animated?: boolean;
}

export const LeftMenu = memo(({ onClose, animated = true }: Props): ReactElement => {
    const newFeaturesTooltipShown = storeHelper.getValue(CLOUD_NEW_FEATURES_TOOLTIP_ID_MOBILE);

    const storage = useSelector(getCurrentStorage);
    const { isReactPage, isAttaches, isPublicOrStock, isAlbums } = getStorage(storage);
    const { goToSubscriptions } = useGoToSubscriptions();

    const scrollRef = useRef(null);
    const featuresRef = useRef(null);

    const [animationFinished, setAnimationFinished] = useState(false);
    const [showTooltip, setShowTooltip] = useState(!newFeaturesTooltipShown);

    const showNewFeaturesIcon = !isAttaches && !isPublicOrStock && !isAlbums;

    useEffect(() => {
        const el = scrollRef?.current;
        scrollLock(el, { reserveScrollBarGap: false });

        return () => {
            scrollUnlock(el);
        };
    }, []);

    const handleGoToSubscriptions = useCallback(() => {
        // TODO: оставить только historyPush после перехода на реакт всех разделов левого меню.
        if (isReactPage) {
            goToSubscriptions();
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.location = '/subscriptions';
        }
        onClose();
    }, [goToSubscriptions, onClose]);

    const handleClickFeatures = useCallback(() => {
        setShowTooltip(false);
        renderMobileCloudNewFeaturesModal();
    }, [setShowTooltip]);

    const handleFinishAnimation = useCallback(() => {
        setAnimationFinished(true);
    }, [setAnimationFinished]);

    const menuItems = useMemo(() => {
        const items: TreeNodeData[] = Object.values(treeItemsFlatten);
        return items
            .filter((item) => item?.isMobile)
            .map(({ id, title, icon, storage }) => (
                <MenuItem key={id} id={id} title={title} icon={icon} storage={storage} onClose={onClose} />
            ));
    }, [treeItemsFlatten, onClose]);

    return (
        <LeftDialog onClose={onClose} animated={animated} onFinishAnimation={handleFinishAnimation}>
            <div className={styles.root}>
                <div className={styles.logo}>
                    <Logo />
                </div>
                {showNewFeaturesIcon && Boolean(CLOUD_NEW_FEATURES_CONFIG.length) && (
                    <div className={styles.newFeatures} ref={featuresRef}>
                        <FeaturesButton onClick={handleClickFeatures} />
                        {animationFinished && showTooltip && <FeaturesTooltip ref={featuresRef} />}
                    </div>
                )}
                <div className={styles.contentWrapper} ref={scrollRef}>
                    <div className={styles.content}>
                        <Space isMobile goToSubscriptions={handleGoToSubscriptions} onClose={onClose} />
                    </div>

                    <div className={styles.menu_items}>{menuItems}</div>
                    <div className={styles.user}>
                        <UserBlock isLeftMenu reloginAction />
                    </div>
                </div>
            </div>
        </LeftDialog>
    );
});

LeftMenu.displayName = 'LeftMenu';
